import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CartService } from '../../services/cart.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslocoModule } from '@jsverse/transloco';
import { TopNavbarComponent } from '../../parkifly-bookings/organizations/components/top-navbar/top-navbar.component';
import { ParkingCardComponent } from '../../parkifly-bookings/components/parking-card/parking-card.component';
import { FormService } from '../../parkifly-bookings/organizations/features/booking/components/form/service';
import { ORGANIZATIONS } from '../../parkifly-bookings/data/organizations';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export interface ParkingItem {
    id?: string;
    parkingId?: string;
    name?: string;
    parkingName?: string;
    organizationId?: string;
    indoors?: boolean;
    largeCars?: boolean;
    largeCar?: boolean;
    isWalkingDistance?: boolean;
    walkingDistance?: boolean;
    price?: any;
    totalPrice?: number;
    discountTotalPrice?: number;
    availableParkingSpots?: number;
    additionalServices?: any[];
    discountCheckAmount?: number;
    discountCheckPercent?: number;
    features?: any[];
    feature1?: any;
    feature2?: any;
    feature3?: any;
    feature4?: any;
    feature5?: any;
    feature6?: any;
    description?: any;
    logoUrl?: string;
    logoClass?: string;
    nrOfDays?: number;
}

@Component({
    selector: 'app-search-results',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        DatePipe,
        RouterLink,
        MatProgressSpinnerModule,
        MatIconModule,
        MatFormFieldModule,
        MatInputModule,
        TranslocoModule,
        TopNavbarComponent,
        ParkingCardComponent
    ],
    templateUrl: './search-results.component.html',
    styleUrls: ['./search-results.component.css']
})
export class SearchResultsComponent implements OnInit {
    stationId: string = '';
    airport: string = 'arlanda';
    fromStr: string = '';
    toStr: string = '';
    fromDate: Date | null = null;
    toDate: Date | null = null;

    parkingDays: number = 1;

    filterCriteriaList: any[] = [];
    optionsFilter: { [key: string]: boolean } = {};

    isLoading: boolean = true;
    parkings: any[] = [];
    rawParkings: ParkingItem[] = [];
    showTransferModal: boolean = false;
    selectedParkingForModal: any = null;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private cartService: CartService,
        private formService: FormService,
        private cdr: ChangeDetectorRef
    ) { }

    ngOnInit(): void {
        this.route.queryParams.subscribe(params => {
            this.stationId = params['stationId'] || '5bc25e00-7863-4b8c-9fe1-eb8af7c7e77e';
            this.airport = params['airport'] || 'arlanda';
            this.fromStr = params['from'] || '';
            this.toStr = params['to'] || '';

            if (this.fromStr) this.fromDate = new Date(this.fromStr);
            if (this.toStr) this.toDate = new Date(this.toStr);

            if (this.fromDate && this.toDate) {
                const diffTime = Math.abs(this.toDate.getTime() - this.fromDate.getTime());
                this.parkingDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) || 1;
            }

            this.optionsFilter = {
                indoorParking: false,
                indorParking: false,
                biggerSpot: false,
                carCharging: false,
                carWashing: false,
                windowRepair: false,
                walkingDistance: false
            };

            this.fetchParkings();
        });
    }

    fetchParkings(): void {
        if (!this.fromStr || !this.toStr) {
            this.isLoading = false;
            return;
        }

        this.isLoading = true;

        if (this.stationId) {
            this.cartService.getAvailableParkingsByStation(this.stationId, this.fromStr, this.toStr, 'NONE').subscribe({
                next: (res: any) => {
                    this.isLoading = false;
                    const list = res?.parkings || res?.availableParkings || (Array.isArray(res) ? res : []);
                    this.rawParkings = list;
                    this.applyFilterAndSort();
                },
                error: (err: any) => {
                    this.isLoading = false;
                    console.error('Error fetching available parkings by station:', err);
                    this.rawParkings = [];
                    this.applyFilterAndSort();
                }
            });

            this.cartService.getAvailableParkingsByStationCriteria().subscribe({
                next: (res: any) => {
                    if (Array.isArray(res) && res.length > 0) {
                        this.filterCriteriaList = res;
                        res.forEach((item: any) => {
                            const name = item.name || item.simpleFieldName || item.translationLabel;
                            if (name && !(name in this.optionsFilter)) {
                                this.optionsFilter[name] = false;
                            }
                        });
                    }
                },
                error: (err: any) => {
                    console.error('Error fetching filtering criteria:', err);
                }
            });
        }
    }

    getCriteriaIcon(item: any): string {
        const key = (item.name || item.translationLabel || '').toLowerCase();
        if (key.includes('indoor') || key.includes('indor')) return 'roofing';
        if (key.includes('bigger') || key.includes('large')) return 'directions_car';
        if (key.includes('charg')) return 'ev_station';
        if (key.includes('wash')) return 'local_car_wash';
        if (key.includes('window') || key.includes('repair')) return 'build';
        if (key.includes('walk')) return 'directions_walk';
        return 'tune';
    }

    toggleOptionFilter(key: string): void {
        this.optionsFilter[key] = !this.optionsFilter[key];
        if (key === 'indorParking') {
            this.optionsFilter['indoorParking'] = this.optionsFilter['indorParking'];
        } else if (key === 'indoorParking') {
            this.optionsFilter['indorParking'] = this.optionsFilter['indoorParking'];
        }
        this.applyFilterAndSort();
    }

    clearAllFilters(): void {
        Object.keys(this.optionsFilter).forEach(key => {
            this.optionsFilter[key] = false;
        });
        this.applyFilterAndSort();
    }

    get hasActiveFilters(): boolean {
        return Object.values(this.optionsFilter).some(v => v);
    }

    applyFilterAndSort(): void {
        const filtered = this.filterParkings(this.rawParkings);
        const mapped = filtered.map(p => this.mapParkingForCard(p));
        mapped.sort((a, b) => (a.totalPrice || 0) - (b.totalPrice || 0));
        this.parkings = mapped;
        this.cdr.detectChanges();
    }

    mapParkingForCard(p: ParkingItem): any {
        const totalPrice = this.getPrice(p);

        const parseFeature = (feature: any) => {
            if (!feature) return null;
            if (typeof feature !== 'string') return feature;
            try {
                let decoded = feature.replace(/&quot;/g, '"').replace(/\\u0022/g, '"');
                return decoded.startsWith('{') ? JSON.parse(decoded) : decoded;
            } catch {
                return feature;
            }
        };

        let features: any[] = [];
        if (p.feature1 || p.feature2 || p.feature3) {
            features = [
                parseFeature(p.feature1),
                parseFeature(p.feature2),
                parseFeature(p.feature3),
                parseFeature(p.feature4),
                parseFeature(p.feature5),
                parseFeature(p.feature6)
            ].filter(f => f != null);
        } else {
            features = [
                p.indoors ? 'Indoor parking' : 'Outdoor parking',
                (p.isWalkingDistance || p.walkingDistance) ? 'Walking distance to terminal' : 'Free Shuttle Transfer Included',
                (p.largeCars || p.largeCar) ? 'Large & wide parking spot' : null,
                'Fenced & 24/7 Monitored Area'
            ].filter(f => f != null);
        }

        return {
            ...p,
            description: p.name || p.parkingName || 'Parking',
            totalPrice: totalPrice,
            logoUrl: this.getOrgLogo(p.organizationId),
            logoClass: this.getOrgLogoClass(p.organizationId),
            availableParkingSpots: p.availableParkingSpots !== undefined ? p.availableParkingSpots : 10,
            features: features,
            nrOfDays: this.parkingDays
        };
    }

    filterParkings(list: ParkingItem[]): ParkingItem[] {
        if (!list || !Array.isArray(list)) return [];

        return list.filter(p => {
            for (const crit of this.filterCriteriaList) {
                const isOptionActive = !!this.optionsFilter[crit.name] ||
                    (crit.name === 'indorParking' && !!this.optionsFilter['indoorParking']) ||
                    (crit.name === 'indoorParking' && !!this.optionsFilter['indorParking']);

                if (isOptionActive) {
                    if (!this.checkParkingCriteria(p, crit)) {
                        return false;
                    }
                }
            }
            return true;
        });
    }

    checkParkingCriteria(p: any, crit: any): boolean {
        // 1. Check simpleFieldName if present
        if (crit.simpleFieldName) {
            const field = crit.simpleFieldName;
            if (p[field] === true) return true;
            if (field === 'indoors' && p.indoors) return true;
            if (field === 'largeCars' && (p.largeCars || p.largeCar)) return true;
            if (field === 'electricalCharging' && (p.electricalCharging || p.carCharging || p.isElectricalCharging)) return true;
            if (field === 'walkingDistance' && (p.walkingDistance || p.isWalkingDistance)) return true;
        }

        // 2. Check additionalServiceProductCode if present
        if (Array.isArray(crit.additionalServiceProductCode) && crit.additionalServiceProductCode.length > 0) {
            const services: any[] = p.additionalServices || p.additionalServiceOptions || p.services || [];
            if (Array.isArray(services) && services.length > 0) {
                const targetCodes: string[] = crit.additionalServiceProductCode.map((c: string) => c.toLowerCase());
                const hasMatchingProduct = services.some(s => {
                    const code = (s?.productCode || s?.serviceProductCode || s?.code || s?.id || '').toString().toLowerCase();
                    return targetCodes.includes(code);
                });
                if (hasMatchingProduct) return true;
            }
        }

        // Fallback keyword search on services if product codes were provided but format differs
        if (Array.isArray(crit.additionalServiceProductCode) && crit.additionalServiceProductCode.length > 0) {
            const services: any[] = p.additionalServices || p.additionalServiceOptions || p.services || [];
            const nameLower = (crit.name || crit.translationLabel || '').toLowerCase();
            let keywords: string[] = [];
            if (nameLower.includes('wash')) keywords = ['wash', 'tvätt', 'recond'];
            if (nameLower.includes('window') || nameLower.includes('repair')) keywords = ['window', 'glas', 'stenskott', 'repair', 'service'];

            if (keywords.length > 0 && services.length > 0) {
                return services.some(s => {
                    const sName = (s?.name || s?.serviceName || s?.productCode || '').toString().toLowerCase();
                    return keywords.some(kw => sName.includes(kw));
                });
            }
        }

        return false;
    }

    getOrgLogo(orgId?: string): string {
        const id = (orgId || '').toLowerCase();
        if (id === 'a848ccd3-fd8d-4035-925e-571ebb4e93e8') {
            return 'assets/img/thumbnail_Flygets-logo.png';
        }
        if (id === '942ce067-7848-4441-b746-8f6eed7395bb') {
            return 'assets/img/quality-hotel-airport-arlanda.svg';
        }
        if (id === '55d7084d-3cf0-4ccc-9448-f924afcdf3bc') {
            return 'assets/img/Aiden-hotel-logo.svg';
        }
        if (id === 'dc3e5c36-a4f7-4886-98f2-4833ea27f84b') {
            return 'assets/img/skavsta-logo.png';
        }
        if (id === 'd9eb0af8-5b3d-4281-8725-372e2d6a7804') {
            return 'assets/img/mdpark_logo.png';
        }
        return 'assets/img/thumbnail_Flygets-logo.png';
    }

    getOrgLogoClass(orgId?: string): string {
        const id = (orgId || '').toLowerCase();
        if (id === '55d7084d-3cf0-4ccc-9448-f924afcdf3bc') return 'logo-aiden';
        if (id === '942ce067-7848-4441-b746-8f6eed7395bb') return 'logo-quality';
        if (id === 'a848ccd3-fd8d-4035-925e-571ebb4e93e8') return 'logo-flygets';
        if (id === 'dc3e5c36-a4f7-4886-98f2-4833ea27f84b') return 'logo-skavsta';
        return 'logo-default';
    }

    getOrgName(orgId?: string): string {
        const id = (orgId || '').toLowerCase();
        if (id === 'a848ccd3-fd8d-4035-925e-571ebb4e93e8') return 'Flygets Long-term Parking';
        if (id === '942ce067-7848-4441-b746-8f6eed7395bb') return 'Quality Hotel Arlanda XPO';
        if (id === '55d7084d-3cf0-4ccc-9448-f924afcdf3bc') return 'Aiden Hotel Arlanda';
        if (id === 'dc3e5c36-a4f7-4886-98f2-4833ea27f84b') return 'Skavsta Parking';
        return 'Parkifly Partner';
    }

    getPrice(p: ParkingItem): number {
        if (typeof p.price === 'number') return p.price;
        if (p.price?.amountIncludeVat) return p.price.amountIncludeVat;
        if (p.price?.amount) return p.price.amount;
        if (p.totalPrice) return p.totalPrice;
        return 650;
    }

    private formatDateForMatrix(date: Date): string {
        if (!date || isNaN(date.getTime())) return '';
        const pad = (n: number) => (n < 10 ? '0' + n : '' + n);
        const yyyy = date.getFullYear();
        const MM = pad(date.getMonth() + 1);
        const dd = pad(date.getDate());
        const hh = pad(date.getHours());
        const mm = pad(date.getMinutes());
        return `${yyyy}-${MM}-${dd}T${hh}:${mm}`;
    }

    selectParking(p: any): void {
        const orgId = (p.organizationId || '').toLowerCase();
        const nameLower = (p.name || p.parkingName || '').toLowerCase();

        // 1. Skavsta redirect flow -> /parkifly/skavsta with prefilled data
        if (orgId === 'dc3e5c36-a4f7-4886-98f2-4833ea27f84b' || nameLower.includes('skavsta')) {
            this.navigateToOrganizationHome('skavsta', p);
            return;
        }

        // 2. Flygets redirect flow -> /parkifly/flygets with prefilled data
        if (orgId === 'a848ccd3-fd8d-4035-925e-571ebb4e93e8' || nameLower.includes('flygets')) {
            this.navigateToOrganizationHome('flygets', p);
            return;
        }

        // 3. MD Park redirect flow -> /parkifly/mdpark with prefilled data
        if (orgId === 'd9eb0af8-5b3d-4281-8725-372e2d6a7804' || nameLower.includes('mdpark')) {
            this.navigateToOrganizationHome('mdpark', p);
            return;
        }

        // 4. Quality or Aiden redirect flow -> Prompt modal for transfers
        if (orgId === '942ce067-7848-4441-b746-8f6eed7395bb' || orgId === '55d7084d-3cf0-4ccc-9448-f924afcdf3bc' || nameLower.includes('quality') || nameLower.includes('aiden')) {
            this.selectedParkingForModal = p;
            this.showTransferModal = true;
            return;
        }

        // Fallback default
        this.navigateToOrganizationHome('flygets', p);
    }

    private navigateToOrganizationHome(orgSlug: string, p: any): void {
        const startDate = this.fromDate || new Date(this.fromStr);
        const endDate = this.toDate || new Date(this.toStr);

        // Bind values to FormService
        this.formService.searchStartDate = startDate;
        this.formService.searchEndDate = endDate;
        this.formService.discountCode = 'NONE';
        this.formService.selectedParking = p;
        if (p) {
            try {
                sessionStorage.setItem('selectedParkingFromSearch', JSON.stringify(p));
                sessionStorage.setItem('isFromCustomerHome', 'true');
            } catch (e) { }
        }

        const parkingControl = this.formService.getForm()?.get('parking.selectedParking');
        if (parkingControl) {
            parkingControl.setValue(p);
        }

        if (p.additionalServices) {
            this.formService.updateFormArray(p.additionalServices);
        }

        const orgObj = ORGANIZATIONS.find(org => org.name.toUpperCase() === orgSlug.toUpperCase());
        if (orgObj) {
            this.formService.organization = orgObj;
        }

        const stateData = {
            departureDate: startDate,
            arrivalDate: endDate,
            flightDepartureDate: startDate,
            arrivalFlightDate: endDate,
            departureFlightDate: startDate,
            discountCode: 'NONE',
            selectedParking: p
        };

        this.router.navigate(['/parkifly', orgSlug], {
            state: stateData
        });
    }

    confirmTransferSelection(includeTransfers: boolean): void {
        this.showTransferModal = false;
        const p = this.selectedParkingForModal;
        this.formService.selectedParking = p;
        if (p) {
            try {
                sessionStorage.setItem('selectedParkingFromSearch', JSON.stringify(p));
                sessionStorage.setItem('isFromCustomerHome', 'true');
            } catch (e) { }
        }

        const orgId = (p.organizationId || '').toLowerCase();
        const nameLower = (p.name || p.parkingName || '').toLowerCase();
        const isAiden = orgId === '55d7084d-3cf0-4ccc-9448-f924afcdf3bc' || nameLower.includes('aiden');

        const startDate = this.fromDate || new Date(this.fromStr);
        const endDate = this.toDate || new Date(this.toStr);
        const parkingId = p.id || p.parkingId || '';

        let targetRoute = '';
        if (includeTransfers) {
            targetRoute = isAiden ? '/parkifly/aiden-parkandfly' : '/parkifly/quality-xpo-arlanda-parkandfly';
        } else {
            targetRoute = isAiden ? '/parkifly/aiden-short-term' : '/parkifly/quality-xpo-arlanda';
        }

        const stateData = {
            departureDate: startDate,
            arrivalDate: endDate,
            departureFlightDate: startDate,
            parkingStartDate: startDate,
            parkingEndDatetime: endDate,
            isDepartureHotel: false,
            isArrivalByFlight: true,
            isArrivalHotel: true,
            discountCode: 'NONE',
            parkingId: parkingId,
            selectedParking: p
        };

        this.router.navigate([targetRoute], {
            state: stateData
        });
    }

    closeTransferModal(): void {
        this.showTransferModal = false;
        this.selectedParkingForModal = null;
    }
}
